import React, { useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import ContentAccessRuleEditor from './access/ContentAccessRuleEditor';

const {
  FiMusic, FiEdit2, FiTrash2, FiEye, FiEyeOff,
  FiDollarSign, FiUsers, FiPlay, FiTag, FiShield, FiChevronDown,
} = FiIcons;

const DropCard = ({ drop, onTogglePublish, onDelete, onEdit, artistId }) => {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const isPWYW = drop.min_price === 0;

  return (
    <div className={`rounded-2xl border overflow-hidden transition-all group ${
      drop.is_published ? 'border-zinc-700 bg-zinc-900/60' : 'border-zinc-800/50 bg-zinc-900/20'
    }`}>
      {/* Cover */}
      <div className="relative h-36 bg-zinc-900 flex items-center justify-center overflow-hidden">
        {drop.cover_url ? (
          <img src={drop.cover_url} alt={drop.title} className="w-full h-full object-cover opacity-80" />
        ) : (
          <SafeIcon icon={FiMusic} className="text-4xl text-zinc-800" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
        <div className={`absolute top-3 left-3 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border ${
          drop.is_published ? 'bg-green-500/20 border-green-500/40 text-green-400' : 'bg-zinc-800 border-zinc-700 text-zinc-500'
        }`}>
          {drop.is_published ? '● Live' : '○ Draft'}
        </div>
        <div className={`absolute top-3 right-3 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border ${
          isPWYW ? 'bg-blue-500/20 border-blue-500/30 text-blue-400' : 'bg-amber-500/20 border-amber-500/30 text-amber-400'
        }`}>
          {isPWYW ? 'PWYW' : `$${drop.min_price.toFixed(2)}+`}
        </div>
        {drop.genre && (
          <div className="absolute bottom-3 left-3 flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-zinc-400">
            <SafeIcon icon={FiTag} className="text-[9px]" /> {drop.genre}
          </div>
        )}
      </div>

      {/* Body */}
      <div className="p-5 space-y-4">
        <div>
          <h4 className="font-black text-white uppercase tracking-tight leading-tight">{drop.title}</h4>
          {drop.description && <p className="text-xs font-bold text-zinc-500 mt-1 line-clamp-2">{drop.description}</p>}
        </div>

        {/* Stats */}
        <div className="flex items-center gap-4 text-[10px] font-black uppercase tracking-widest text-zinc-600">
          <span className="flex items-center gap-1"><SafeIcon icon={FiPlay} className="text-[9px]" /> {drop.play_count || 0} plays</span>
          <span className="flex items-center gap-1"><SafeIcon icon={FiUsers} className="text-[9px]" /> {drop.unlock_count || 0} unlocks</span>
          <span className="flex items-center gap-1 text-green-600"><SafeIcon icon={FiDollarSign} className="text-[9px]" /> ${(drop.suggested_price || 5).toFixed(2)}</span>
        </div>

        {drop.tags?.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {drop.tags.slice(0, 4).map(t => (
              <span key={t} className="px-2 py-0.5 bg-zinc-800 border border-zinc-700/50 rounded-full text-[9px] font-black uppercase tracking-widest text-zinc-500">{t}</span>
            ))}
          </div>
        )}

        {/* Actions */}
        {confirmDelete ? (
          <div className="flex items-center gap-2">
            <p className="text-xs font-black text-red-400 flex-1">Delete permanently?</p>
            <button onClick={() => { onDelete(drop.id); setConfirmDelete(false); }}
              className="px-3 py-2 bg-red-500/20 border border-red-500/40 text-red-400 text-[10px] font-black uppercase tracking-widest rounded-xl hover:bg-red-500/30 transition-all">Yes</button>
            <button onClick={() => setConfirmDelete(false)}
              className="px-3 py-2 border border-zinc-700 text-zinc-400 text-[10px] font-black uppercase tracking-widest rounded-xl hover:border-zinc-600 transition-all">No</button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <button onClick={() => onTogglePublish(drop.id, !drop.is_published)}
              className={`flex items-center gap-1.5 px-4 py-2.5 rounded-xl border text-[10px] font-black uppercase tracking-widest transition-all flex-1 justify-center ${
                drop.is_published ? 'border-zinc-700 text-zinc-400 hover:border-zinc-600' : 'bg-green-500/10 border-green-500/30 text-green-400 hover:bg-green-500/20'
              }`}>
              <SafeIcon icon={drop.is_published ? FiEyeOff : FiEye} className="text-xs" />
              {drop.is_published ? 'Unpublish' : 'Publish'}
            </button>
            <button onClick={() => onEdit(drop)}
              className="p-2.5 rounded-xl border border-zinc-800 text-zinc-500 hover:text-zinc-300 hover:border-zinc-600 transition-all">
              <SafeIcon icon={FiEdit2} className="text-sm" />
            </button>
            <button onClick={() => setConfirmDelete(true)}
              className="p-2.5 rounded-xl border border-zinc-800 text-zinc-500 hover:text-red-400 hover:border-red-500/40 transition-all">
              <SafeIcon icon={FiTrash2} className="text-sm" />
            </button>
          </div>
        )}

        {/* Access Rules toggle */}
        {artistId && (
          <button onClick={() => setShowRules(s => !s)}
            className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl border text-[10px] font-black uppercase tracking-widest transition-all ${
              showRules ? 'border-indigo-500/40 bg-indigo-500/5 text-indigo-400' : 'border-zinc-800 text-zinc-600 hover:border-zinc-700 hover:text-zinc-400'
            }`}>
            <div className="flex items-center gap-1.5">
              <SafeIcon icon={FiShield} className="text-xs" /> Access Rules
            </div>
            <SafeIcon icon={FiChevronDown} className={`text-xs transition-transform ${showRules ? 'rotate-180' : ''}`} />
          </button>
        )}

        {showRules && artistId && (
          <ContentAccessRuleEditor
            contentId={drop.id}
            contentType="drop"
            artistId={artistId}
            onClose={() => setShowRules(false)}
          />
        )}
      </div>
    </div>
  );
};

export default DropCard;